import { Injectable } from '@angular/core';
import { CollectionReference, Query } from '@firebase/firestore-types';
import * as firebase from 'firebase/app';

import { Page, PageSpecs, SortOrder, Filter } from '../pagination/page.model';
import { Member, MemberData } from '../domain/member.model';
import { Season } from '../domain/season.model';

import * as moment from 'moment';

@Injectable({
  providedIn: 'root',
})
export class MemberService {
  private members: CollectionReference;

  constructor() {
    this.members = firebase.firestore().collection("members");
  }

  async getMembersCount():Promise<number>{
    let full = await this.members.get();
    return full.size;
  }

  async getCoaches(): Promise<Member[]>{
    let s = await this.members.where('coach', '==', true).get();
    return s.docs.map(Member.of);
  }

  async getFirstPage(specs:PageSpecs<MemberFilter>): Promise<Page<Member>>{
    let snapshot = await specs.filter.where(this.members)
      .orderBy(specs.sort, specs.order)
      .limit(specs.size)
      .get();
    let full = await specs.filter.where(this.members).get();
    let data = snapshot.docs.map(Member.of);
    return {
      _embedded: data,
      count: data.length,
      items: data,
      current: specs.page,
      size: specs.size,
      total: full.size
    }
  }
  async getAllMembers(filter: MemberFilter, sort: string, order: SortOrder): Promise<Member[]>{
    const snapshot =  await filter
      .where(this.members)
      .orderBy(sort, order)
      .get()
    return snapshot.docs.map(Member.of)
  }

  async getPreviousPage(page: Page<Member>, specs:PageSpecs<MemberFilter>): Promise<Page<Member>>{
    if (specs.page==1){
      return this.getFirstPage(specs);
    } else {
      let first = await this.members.doc(page.items[0].id).get();
      let snapshot = await specs.filter.where(this.members)
        .orderBy(specs.sort, specs.order)
        .endBefore(first).limit(specs.size).get();
      let data = snapshot.docs.map(Member.of);
      return {
        _embedded: data,
        count: data.length,
        items: data,
        current: specs.page,
        size: specs.size,
        total: page.total
      }
    }
  }
  async getNextPage(page: Page<Member>, specs:PageSpecs<MemberFilter>): Promise<Page<Member>>{
    let last = await this.members.doc(page.items[page.items.length -1].id).get();
    let snapshot = await specs.filter.where(this.members)
      .orderBy(specs.sort, specs.order)
      .startAfter(last)
      .limit(specs.size)
      .get();
    let data = snapshot.docs.map(Member.of);
    return {
      _embedded: data,
      count: data.length,
      items: data,
      current: specs.page,
      size: specs.size,
      total: page.total
    }
  }
  async getLastPage(specs:PageSpecs<MemberFilter>): Promise<Page<Member>>{
    let snapshot = await specs.filter.where(this.members)
      .orderBy(specs.sort, specs.order==SortOrder.asc?SortOrder.desc:SortOrder.asc)
      .limit(specs.size)
      .get();
    let full = await specs.filter.where(this.members).get();
    let data = snapshot.docs.map(Member.of);
    return {
      _embedded: data,
      count: data.length,
      items: data.reverse(),
      current: specs.page,
      size: specs.size,
      total: full.size
    }
  }
  async attachSeason(member:Member, season:Season): Promise<Member>{
    if (member.data.seasons == undefined){
      member.data.seasons = [];
    }
    member.data.seasons.push(season.id);
    await this.members.doc(member.id).update({
      'seasons': member.data.seasons,
      'membership_date': moment().toDate()});
    return member;
  }

  async detachSeason(member:Member, season:Season): Promise<Member>{
    if (member.data.seasons == undefined){
      member.data.seasons = [];
    }
    member.data.seasons = member.data.seasons.filter(e=>e != season.id);
    await this.members.doc(member.id).update({
      'seasons': member.data.seasons,
      'membership_date': null
    });
    return member;
  }

  async getSeasonMembersCount(season: Season): Promise<number>{
    console.log("getSeasonMembersCount");
    let seasonMember = await this.members.where('seasons', 'array-contains', season.id).get();
    return seasonMember.size;
  }

  async getMember(id:string): Promise<Member>{
    let snapshot = await this.members.doc(id).get();
    return Member.of(snapshot);
  }

  async patchMember(member: Member, changes: any): Promise<void>{
    this.normalizePhoneNumbers(changes);
    if (changes.last_name){
      changes.last_name = changes.last_name.toUpperCase();
    }
    await this.members.doc(member.id).update(changes);
    return null;
  }

  async createMember(member: MemberData): Promise<Member>{
    this.normalizePhoneNumbers(member);
    member.last_name = member.last_name.toUpperCase();
    let reference = await this.members.add(Object.assign({}, member));
    let snapshot = await reference.get();
    return Member.of(snapshot);
  }

  private normalizePhoneNumbers(member: MemberData){
    if (member.phone1){
      member.phone1 = this.normalizePhoneNumber(member.phone1);
    }
    if (member.phone2) {
      member.phone2 = this.normalizePhoneNumber(member.phone2);
    }
  }

  private normalizePhoneNumber(phone: string){
    if (!phone){
      return phone;
    }
    phone = phone.replace(/^00/, '+');
    phone = phone.replace(/^0/, '+33');
    phone = phone.replace(/[\s|\.]/g, '');
    return phone;
  }
}

export class MemberFilter implements Filter{
  constructor(private s:string, private q:string, private memberToday:boolean, private createdToday:boolean){};
  where(query:Query):Query{
    if (this.s){
      query = query.where('seasons', 'array-contains', this.s);
    }
    if (this.q){
      query = query.where('last_name', '>=', this.q).where('last_name', '<=', this.q+ '\uf8ff');
    }
    const from = moment().startOf('day').toDate();
    const to = moment().endOf('day').toDate();
    if (this.memberToday){
      query = query
        .where('membership_date', '>=', from)
        .where('membership_date', '<=', to)
        .orderBy('membership_date')
    }
    if (this.createdToday){
      query = query
        .where('created_at', '>=', from)
        .where('created_at', '<=', to)
        .orderBy('created_at')
    }
    return query;
  }

  addParams(params:any){
    params.s = this.s;
    params.q = this.q;
  }
}
